/**
 * Shared QR encoding for every OmniMedia surface.
 *
 * The platform used to carry three separate QR libraries — `lean-qr` (web
 * player widget), `qrcode` (label PDFs) and `react-native-qrcode-svg` (tv
 * pairing) — and still left the shared native signage widget with none, so a
 * venue's `qr` signage tile rendered as unscannable plain text on tv and
 * mobile. This module is the one encoder everything now goes through.
 *
 * `lean-qr` is the pick: zero dependencies, pure JS, no canvas/DOM assumption,
 * so the identical code path runs under node --test, in the browser, and in
 * React Native.
 */
import { generate, correction } from "lean-qr";

export interface QrMatrix {
  /** Module count per side (excludes the quiet zone). */
  size: number;
  /** `true` when the module at (row, col) is dark. */
  isDark(row: number, col: number): boolean;
}

/**
 * Quiet zone required by the QR spec, in modules. Scanners need it on all four
 * sides; renderers must include it or codes fail to read against busy signage
 * backgrounds.
 */
export const QR_QUIET_ZONE = 4;

/**
 * Build a QR module matrix for `text`.
 *
 * Error-correction level M balances density against robustness — the same
 * level the label-PDF path used with `qrcode`, so printed device labels keep
 * their previous fidelity.
 *
 * Returns `null` when the payload cannot be encoded (too long for version 40);
 * callers render their own fallback rather than showing a broken code.
 */
export function qrMatrix(text: string): QrMatrix | null {
  if (!text) return null;
  try {
    const code = generate(text, {
      minCorrectionLevel: correction.M,
      maxCorrectionLevel: correction.M,
    });
    return {
      size: code.size,
      // lean-qr indexes get(x, y) as (column, row); QrMatrix is (row, col).
      isDark: (row, col) => code.get(col, row),
    };
  } catch {
    return null;
  }
}

/**
 * Render a matrix as a single SVG path `d` string, one subpath per dark module,
 * in a viewBox of `size + 2 * QR_QUIET_ZONE` units.
 *
 * A single path rather than N `<Rect>` elements matters: a version-10 code is
 * ~3,600 modules, and thousands of RN view nodes visibly stall the cheap
 * Android boxes the tv shell targets. One path node draws the same pixels.
 */
export function qrPath(matrix: QrMatrix): string {
  const parts: string[] = [];
  for (let row = 0; row < matrix.size; row++) {
    for (let col = 0; col < matrix.size; col++) {
      if (matrix.isDark(row, col)) {
        parts.push(`M${col + QR_QUIET_ZONE} ${row + QR_QUIET_ZONE}h1v1h-1z`);
      }
    }
  }
  return parts.join("");
}

/** Side length of the rendered code including both quiet zones, in modules. */
export function qrViewBoxSize(matrix: QrMatrix): number {
  return matrix.size + QR_QUIET_ZONE * 2;
}

const BASE64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

/**
 * Minimal base64 for ASCII strings (the generated SVG is pure ASCII).
 * Hand-rolled because React Native has neither `btoa` nor `Buffer`, and the
 * data URL MUST be base64: Android image loaders (expo-image/Glide) reject
 * percent-encoded `data:image/svg+xml,` URIs, which would leave the QR blank
 * on Android TV and Android phones while iOS renders it fine.
 */
function base64Ascii(input: string): string {
  let out = "";
  for (let i = 0; i < input.length; i += 3) {
    const a = input.charCodeAt(i);
    const b = i + 1 < input.length ? input.charCodeAt(i + 1) : 0;
    const c = i + 2 < input.length ? input.charCodeAt(i + 2) : 0;
    const triplet = (a << 16) | (b << 8) | c;
    out +=
      BASE64_ALPHABET[(triplet >> 18) & 63] +
      BASE64_ALPHABET[(triplet >> 12) & 63] +
      (i + 1 < input.length ? BASE64_ALPHABET[(triplet >> 6) & 63] : "=") +
      (i + 2 < input.length ? BASE64_ALPHABET[triplet & 63] : "=");
  }
  return out;
}

/** Render an already-encoded matrix as a base64 SVG data URL. */
export function qrSvgDataUrlFromMatrix(matrix: QrMatrix): string {
  const box = qrViewBoxSize(matrix);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${box} ${box}" shape-rendering="crispEdges">` +
    `<rect width="${box}" height="${box}" fill="#fff"/>` +
    `<path d="${qrPath(matrix)}" fill="#000"/>` +
    `</svg>`;
  return `data:image/svg+xml;base64,${base64Ascii(svg)}`;
}

/**
 * Encode `text` as an SVG data URL suitable for an `<img src>`.
 *
 * Vector, so it stays crisp at any size, and pure string work — no canvas, no
 * DOM, no `btoa` (absent in React Native). Returns `null` when the payload
 * cannot be encoded.
 */
export function qrSvgDataUrl(text: string): string | null {
  const matrix = qrMatrix(text);
  if (!matrix) return null;
  return qrSvgDataUrlFromMatrix(matrix);
}
